/**
 * CbOnePager — the one-page CB Summary for an estimate.
 *
 * Computed LIVE from the discipline summaries + indirects + site expenses,
 * with per-cost-type markup applied on top. Nothing here is stored, so it can
 * never drift out of sync with the sheets underneath it.
 *
 * Click any discipline column header to drill into that discipline's summary.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCbApi, useCbConfig } from './cbApi';
import {
  SheetShell, HeaderButton, ActionButton, HelpModal,
  LoadingState, ErrorState, EmptyState, NoEstimateState, useToast, fmt,
} from './CbUi';
import AiAccuracyCurve from './AiAccuracyCurve';
import CbAuditLogButton from './CbAuditLogButton';

const ACCENT = '#0f766e';

const CbOnePager = ({ estimate, onOpenDiscipline }) => {
  const api = useCbApi();
  const { config } = useCbConfig();
  const { showToast, ToastEl } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showMarkup, setShowMarkup] = useState(false);
  const [markupDraft, setMarkupDraft] = useState({});
  const [savingMarkup, setSavingMarkup] = useState(false);
  const [showUsd, setShowUsd] = useState(false);

  const fetchData = useCallback(async () => {
    if (!estimate?.id) return;
    try {
      setLoading(true);
      const qs = showUsd ? '?currency=USD' : '';
      const res = await api.get(`/cost-breakdown/one-pager/${estimate.id}${qs}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, estimate?.id, showUsd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Disciplines that actually have data on this estimate (columns of the matrix)
  const disciplineCols = useMemo(() => {
    if (!data) return [];
    const labels = new Set();
    for (const ct of Object.values(data.matrix || {})) {
      for (const label of Object.keys(ct)) {
        if (label !== '__all__') labels.add(label);
      }
    }
    const order = (config?.disciplines || []).map((d) => d.label);
    return [...labels].sort((a, b) => {
      const ia = order.indexOf(a); const ib = order.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [data, config]);

  const discByLabel = useMemo(() => {
    const map = {};
    (config?.disciplines || []).forEach((d) => { map[d.label] = d; });
    return map;
  }, [config]);

  const costTypes = config?.costTypes || [];

  const openMarkup = useCallback(() => {
    const draft = {};
    for (const ct of costTypes) {
      draft[ct.key] = data?.costTypeTotals?.[ct.key]?.markup_pct ?? 0;
    }
    setMarkupDraft(draft);
    setShowMarkup(true);
  }, [costTypes, data]);

  const saveMarkup = useCallback(async () => {
    setSavingMarkup(true);
    try {
      await api.put(`/cost-breakdown/markup/${estimate.id}`, {
        markups: Object.entries(markupDraft).map(([cost_type, markup_pct]) => ({ cost_type, markup_pct })),
      });
      showToast('Markup saved');
      setShowMarkup(false);
      fetchData();
    } catch (err) {
      showToast(`Failed to save markup: ${err.message}`, 'error');
    } finally {
      setSavingMarkup(false);
    }
  }, [api, estimate?.id, markupDraft, fetchData, showToast]);

  if (!estimate?.id) return <NoEstimateState section="CB Summary" />;
  if (loading && !data) return <LoadingState label="Computing CB Summary…" />;
  if (error) return <ErrorState error={error} onRetry={fetchData} />;
  if (!data) return null;

  const totals = data.totals || { man_hours: 0, dollar_cost: 0, selling: 0 };
  const hasAnything = totals.dollar_cost !== 0 || totals.man_hours !== 0;
  const margin = totals.selling - totals.dollar_cost;
  const currency = data.currency || {};
  const currencyLabel = currency.display === 'USD' ? 'USD' : 'Site $';

  const cellStyle = { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', whiteSpace: 'nowrap', fontSize: 12.5 };

  return (
    <>
      <SheetShell
        title="📄 CB Summary — One Pager"
        subtitle={`Estimate ${estimate.estimate_number} · ${estimate.estimate_name || ''} · computed live · display ${currencyLabel}${currency.cad_to_usd_rate ? ` · FX ${currency.cad_to_usd_rate}${currency.is_frozen ? ' frozen' : ''}` : ''}`}
        gradient={`linear-gradient(135deg, ${ACCENT}, #14b8a6)`}
        headerActions={
          <>
            <HeaderButton
              onClick={() => setShowUsd((v) => !v)}
              title={currency.note || 'Toggle site currency vs frozen CAD→USD (MTO FX rate)'}
            >
              {showUsd ? '💱 Show Site $' : '💱 Show USD'}
            </HeaderButton>
            <HeaderButton onClick={openMarkup}>💹 Markup</HeaderButton>
            <HeaderButton onClick={fetchData}>🔄 Refresh</HeaderButton>
            <CbAuditLogButton
              tableName={['cb_discipline_summary', 'cb_indirects', 'cb_site_expenses', 'cb_markup', 'cb_discipline_detail']}
              estimateId={estimate?.id}
              title="CB Package Audit Log"
              accentColor={ACCENT}
            />
            <HeaderButton onClick={() => setShowHelp(true)}>❓ Help</HeaderButton>
          </>
        }
        kpiCards={[
          { label: 'Total Man Hours', value: fmt(totals.man_hours, 'number'), icon: '⏱️', color: '#059669' },
          { label: `Total Cost (${currencyLabel})`, value: fmt(totals.dollar_cost, 'currency'), icon: '💰', color: '#dc2626' },
          { label: `Selling (${currencyLabel})`, value: fmt(totals.selling, 'currency'), icon: '🏷️', color: ACCENT },
          { label: 'Margin', value: fmt(margin, 'currency'), icon: '📈', color: margin >= 0 ? '#059669' : '#dc2626' },
        ]}
        banner={(
          <div style={{ padding: '12px 32px 0', background: '#f3f4f6' }}>
            {currency.note && (
              <div style={{
                marginBottom: 10, padding: '10px 14px', borderRadius: 10,
                background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', fontSize: 13,
              }}>
                {currency.note}
              </div>
            )}
            <AiAccuracyCurve estimate={estimate} />
          </div>
        )}
      >
        {!hasAnything ? (
          <EmptyState icon="📄" title="Nothing to summarize yet">
            <p>The one-pager aggregates the <strong>Discipline Summaries</strong>, <strong>Indirects</strong> and <strong>Site Expenses</strong> of this estimate.</p>
            <p>Start in a discipline's <strong>Detailed Estimation</strong> sheet, roll it up, and the numbers appear here automatically.</p>
          </EmptyState>
        ) : (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, fontSize: 11.5, borderBottom: '2px solid #e2e8f0' }}>Cost Type</th>
                  {disciplineCols.map((label) => {
                    const d = discByLabel[label];
                    return (
                      <th
                        key={label}
                        onClick={() => d && onOpenDiscipline && onOpenDiscipline(d.key)}
                        title={d && onOpenDiscipline ? `Open ${label} summary` : undefined}
                        style={{
                          ...cellStyle, fontWeight: 600, fontSize: 11.5, borderBottom: '2px solid #e2e8f0',
                          cursor: d && onOpenDiscipline ? 'pointer' : 'default',
                          color: d?.color || '#374151',
                        }}
                      >
                        {d?.icon || ''} {label}
                      </th>
                    );
                  })}
                  <th style={{ ...cellStyle, fontWeight: 700, fontSize: 11.5, borderBottom: '2px solid #e2e8f0', background: '#f1f5f9' }}>Cost</th>
                  <th style={{ ...cellStyle, fontWeight: 600, fontSize: 11.5, borderBottom: '2px solid #e2e8f0' }}>Markup</th>
                  <th style={{ ...cellStyle, fontWeight: 700, fontSize: 11.5, borderBottom: '2px solid #e2e8f0', background: '#ecfdf5', color: ACCENT }}>Selling</th>
                </tr>
              </thead>
              <tbody>
                {costTypes.map((ct, idx) => {
                  const rowTotals = data.costTypeTotals?.[ct.key] || { man_hours: 0, dollar_cost: 0, markup_pct: 0, selling: 0 };
                  if (rowTotals.dollar_cost === 0 && rowTotals.man_hours === 0) return null;
                  const cells = data.matrix?.[ct.key] || {};
                  const estimateWide = cells.__all__;
                  return (
                    <tr key={ct.key} style={{ background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                      <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 600, color: '#1f2937' }}>{ct.label}</td>
                      {disciplineCols.map((label) => {
                        const cell = cells[label];
                        return (
                          <td key={label} style={cellStyle}>
                            {cell ? fmt(cell.dollar_cost, 'currency') : ''}
                          </td>
                        );
                      })}
                      {/* estimate-wide values (indirects, site expenses) show only in the Cost column */}
                      <td style={{ ...cellStyle, fontWeight: 700, background: '#f8fafc' }}>
                        {fmt(rowTotals.dollar_cost, 'currency')}
                        {estimateWide && disciplineCols.length > 0 ? ' *' : ''}
                      </td>
                      <td style={{ ...cellStyle, color: rowTotals.markup_pct ? '#7c3aed' : '#9ca3af' }}>
                        {rowTotals.markup_pct ? `${rowTotals.markup_pct}%` : '—'}
                      </td>
                      <td style={{ ...cellStyle, fontWeight: 700, background: '#f0fdfa', color: ACCENT }}>
                        {fmt(rowTotals.selling, 'currency')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#0f172a', color: '#fff' }}>
                  <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 700, borderBottom: 'none', color: '#fff' }}>TOTAL</td>
                  {disciplineCols.map((label) => {
                    let colTotal = 0;
                    for (const ct of costTypes) {
                      const cell = data.matrix?.[ct.key]?.[label];
                      if (cell) colTotal += cell.dollar_cost || 0;
                    }
                    return <td key={label} style={{ ...cellStyle, fontWeight: 700, borderBottom: 'none', color: '#fff' }}>{fmt(colTotal, 'currency')}</td>;
                  })}
                  <td style={{ ...cellStyle, fontWeight: 700, borderBottom: 'none', color: '#fff' }}>{fmt(totals.dollar_cost, 'currency')}</td>
                  <td style={{ ...cellStyle, borderBottom: 'none' }} />
                  <td style={{ ...cellStyle, fontWeight: 700, borderBottom: 'none', color: '#5eead4' }}>{fmt(totals.selling, 'currency')}</td>
                </tr>
              </tfoot>
            </table>
            <p style={{ padding: '8px 14px', margin: 0, fontSize: 11.5, color: '#6b7280' }}>
              * Indirects and Site Expenses are estimate-wide and appear in the Cost column only.
              Click a discipline header to drill into its summary. Man hours total: <strong>{fmt(totals.man_hours, 'number')}</strong>.
            </p>
          </div>
        )}
        {ToastEl}
      </SheetShell>

      {/* Markup editor */}
      {showMarkup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }} onClick={() => setShowMarkup(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '92%', maxWidth: 480, maxHeight: '84vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '16px 24px', background: ACCENT, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>💹 Markup by Cost Type</h2>
              <button onClick={() => setShowMarkup(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#fff' }}>×</button>
            </div>
            <div style={{ padding: 24, overflowY: 'auto' }}>
              {costTypes.map((ct) => (
                <div key={ct.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <span style={{ fontSize: 13.5, color: '#374151' }}>{ct.label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="number"
                      step="0.5"
                      value={markupDraft[ct.key] ?? 0}
                      onChange={(e) => setMarkupDraft((prev) => ({ ...prev, [ct.key]: e.target.value }))}
                      style={{ width: 90, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, textAlign: 'right' }}
                    />
                    <span style={{ fontSize: 13, color: '#6b7280' }}>%</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowMarkup(false)} style={{ padding: '9px 18px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <ActionButton color={ACCENT} onClick={saveMarkup} disabled={savingMarkup}>
                {savingMarkup ? 'Saving…' : 'Save Markup'}
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="CB Summary Help" accent={ACCENT} guideId="cb-one-pager-help">
        <p><strong>This is the one-page summary</strong> of estimate <strong>{estimate.estimate_number}</strong> — the sheet you'd hand to management or price a bid from.</p>
        <h4>How it's built</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li>Every cell is <strong>computed live</strong> from the Discipline Summaries, Indirects, and Site Expenses. There is no stored copy that can drift out of date.</li>
          <li>Rows are cost types (Direct Labour, Materials, Sub-Trades…); columns are the disciplines that have data on this estimate.</li>
          <li><strong>Indirects</strong> and <strong>Site Expenses</strong> are estimate-wide, so they appear only in the Cost column (marked *).</li>
        </ul>
        <h4>Markup &amp; selling price</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li>Click <strong>Markup</strong> to set a percentage per cost type — e.g. ten percent on labour, five on materials.</li>
          <li>Selling = Cost × (1 + markup). The Margin KPI shows the resulting profit.</li>
        </ul>
        <h4>Drill-down</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li>Click a <strong>discipline column header</strong> to open that discipline's summary worksheet.</li>
          <li>If a number looks off, follow it down: one-pager → discipline summary → detailed estimation.</li>
        </ul>
      </HelpModal>
    </>
  );
};

export default CbOnePager;
